import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { useState } from "react";

import { useWorkspaceConnections } from "@web-app/api";
import { Avatar, Button, Icon, Modal } from "@web-app/components";
import { Workspace } from "@web-app/types";

export default function SendToModal({
  isBothIndividualAndRoutedOrderSelected,
  isRoutedOrderSelectedWithoutFullRoute,
  onClose,
  onSend,
}: {
  isBothIndividualAndRoutedOrderSelected: boolean;
  isRoutedOrderSelectedWithoutFullRoute: boolean;
  onClose: () => void;
  onSend: (selectedWorkspaceId: string) => Promise<void>;
}) {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const onConfirm = async () => {
    try {
      setIsLoading(true);
      await onSend(selectedWorkspaceId);
      setIsLoading(false);
      onClose();
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={
        <>
          <Icon name="circleExclamation" className=" text-red-600 mr-2" />
          Send Orders
        </>
      }
      isOpen={true}
      onClose={onClose}
      noClose={true}
      width={400}
      actionsAlign="end"
      actions={[
        <Button plain onClick={onClose}>
          {!isBothIndividualAndRoutedOrderSelected &&
          !isRoutedOrderSelectedWithoutFullRoute
            ? "Cancel"
            : "Got it"}
        </Button>,
        !isBothIndividualAndRoutedOrderSelected &&
          !isRoutedOrderSelectedWithoutFullRoute && (
            <Button
              type="button"
              isLoading={isLoading}
              onClick={onConfirm}
              className="bg-black-600 hover:bg-black-700 text-white"
            >
              Confirm
            </Button>
          ),
      ]}
    >
      {isBothIndividualAndRoutedOrderSelected ? (
        <p className="text-sm font-normal leading-5 p-3">
          Bulk assign and send are not allowed for a combined selection of
          Routed and P2P orders. Please remove one to proceed.
        </p>
      ) : isRoutedOrderSelectedWithoutFullRoute ? (
        <p className="text-sm font-normal leading-5 p-3">
          Selection includes routed orders. Bulk assignment and send apply to
          full routes only.
        </p>
      ) : (
        <div className="p-3">
          <WorkspaceSelector
            onChange={(workspace) => setSelectedWorkspaceId(workspace.id)}
          />
        </div>
      )}
    </Modal>
  );
}

function WorkspaceSelector({
  onChange,
}: {
  onChange: (workspace: Workspace) => void;
}) {
  const [query, setQuery] = useState("");
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(
    null,
  );
  const { data: workspaceConnections } = useWorkspaceConnections();

  const filteredWorkspaceConnections =
    query === ""
      ? workspaceConnections
      : workspaceConnections?.filter((workspaceConnection) => {
          return workspaceConnection.connectedWorkspace.name
            .toLowerCase()
            .includes(query.toLowerCase());
        });

  const onSelectWorkspace = (workspace: Workspace | null) => {
    if (!workspace) return;
    setSelectedWorkspace(workspace);
    onChange(workspace);
  };

  return (
    <Combobox
      value={selectedWorkspace}
      immediate
      onChange={onSelectWorkspace}
      onClose={() => setQuery("")}
      as="div"
      className="relative w-full"
    >
      <div className="relative">
        <ComboboxInput
          aria-label="Assignee"
          displayValue={(selectedWorkspace: Workspace) =>
            selectedWorkspace?.name
          }
          placeholder="Select a workspace"
          onChange={(event) => setQuery(event.target.value)}
          className="box-border h-[38px] w-full rounded-md bg-gray-100 px-2 py-3 text-sm leading-5 text-gray-900 outline-none transition ease-in placeholder:text-gray-500 hover:bg-white focus:bg-white focus:ring-2 focus:ring-gray-300 disabled:pointer-events-none disabled:bg-gray-50 disabled:text-gray-400"
        />
        <ComboboxOptions anchor="bottom" className="bg-white w-[350px]">
          <ComboboxOption
            value={{ id: "", name: "None" }}
            className="data-[focus]:bg-blue-100 cursor-pointer"
          >
            <div className="flex gap-x-4 items-center p-2">
              <div className="">
                <p className="text-sm">None</p>
              </div>
            </div>
          </ComboboxOption>
          {(filteredWorkspaceConnections || []).map((workspaceConnection) => (
            <ComboboxOption
              key={workspaceConnection.id}
              value={workspaceConnection.connectedWorkspace}
              className="data-[focus]:bg-blue-100 cursor-pointer"
            >
              <div className="flex gap-x-4 items-center p-2">
                <Avatar
                  name={workspaceConnection.connectedWorkspace.name}
                  size={8}
                  image={workspaceConnection.connectedWorkspace.avatarUrl ?? ""}
                />
                <div className="">
                  <p className="text-sm">
                    {workspaceConnection.connectedWorkspace.name}
                  </p>
                </div>
              </div>
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}
